import { Grid, TextField as MUITextField } from "@mui/material";
import MuiAutocomplete, {
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import {
  AutocompleteProps,
  fieldToAutocomplete,
  fieldToTextField,
} from "formik-mui";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { JobTask } from "../tasks/JobTaskService";
import { formatTaskOptionLabel } from "../tasks/formatTaskOptionLabel";
import { ITask } from "../tasks/types";

export interface TaskAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  label?: string;
}
const FormikTaskAutoComplete = <
  // eslint-disable-next-line
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>(
  props: TaskAutocompleteProps<ITask, Multiple, DisableClearable, FreeSolo>
) => {
  const { form, label = "Task" } = props;
  const [tasks, setTasks] = useState<ITask[]>([]);
  const { name } = fieldToTextField(props as any);
  const prevJobIdRef = useRef<string | number | undefined | null>(undefined);

  useEffect(() => {
    let active = true;
    const jobId = form.values.job_id;
    const prevJobId = prevJobIdRef.current;

    // Clear task only when the job actually changes (not on first mount / edit open)
    if (
      prevJobId !== undefined &&
      prevJobId !== null &&
      String(prevJobId) !== String(jobId ?? "")
    ) {
      form.setFieldValue("task_id", "");
      form.setFieldValue("task", null);
    }
    prevJobIdRef.current = jobId;

    if (parseInt(jobId)) {
      const preserveTaskId = form.values.task_id;
      JobTask.query(jobId)
        .then((loadedTasks) => {
          if (!active) return;
          setTasks(loadedTasks);
          if (preserveTaskId != null && preserveTaskId !== "" && Number(preserveTaskId) !== 0) {
            const matched = loadedTasks.find(
              (t) => String(t.id) === String(preserveTaskId)
            );
            if (matched) {
              form.setFieldValue("task", matched);
              form.setFieldValue("task_id", matched.id);
            }
          }
        })
        .catch(() => {
          enqueueSnackbar(`Error fetching tasks`, {
            variant: "error",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center",
            },
          });
        });
    } else {
      setTasks([]);
    }
    return () => {
      active = false;
    };
    // eslint-disable-next-line
  }, [form.values.job_id, form.setFieldValue]);

  // Keep Autocomplete value in sync when task_id is set but task object was cleared (e.g. form reinit)
  useEffect(() => {
    const taskId = form.values.task_id;
    if (
      !tasks.length ||
      taskId == null ||
      taskId === "" ||
      Number(taskId) === 0
    ) {
      return;
    }
    if (form.values.task && String(form.values.task.id) === String(taskId)) {
      return;
    }
    const matched = tasks.find((t) => String(t.id) === String(taskId));
    if (matched) {
      form.setFieldValue("task", matched);
    }
    // eslint-disable-next-line
  }, [tasks, form.values.task_id, form.values.task]);

  return (
    <>
      <MuiAutocomplete
        {...fieldToAutocomplete(props)}
        fullWidth
        options={tasks}
        autoComplete
        includeInputInList
        onChange={(_event, newValue) => {
          form.setFieldValue("task", newValue);
          if (typeof newValue === "string") {
            form.setFieldValue("task_id", newValue);
          } else {
            // @ts-ignore
            form.setFieldValue("task_id", newValue && newValue.id ? newValue.id : "");
          }
        }}
        getOptionLabel={(option) => {
          if (typeof option === "string") {
            return option;
          }
          return formatTaskOptionLabel(option) || "";
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <MUITextField
            {...params}
            error={form.touched[name!] && !!form.errors[name!]}
            // @ts-ignore
            helperText={form.touched[name!] && form.errors[name!]}
            label={label}
          />
        )}
        renderOption={(props, option, state) => {
          const matches = match(`${option.id}`!, state.inputValue);
          const jobs = parse(`${option.id}`!, matches);

          return (
            <li {...props}>
              <Grid container alignItems="center">
                <Grid item xs>
                  {jobs.map((job, index) => (
                    <span
                      key={index}
                      style={{
                        fontWeight: job.highlight ? 700 : 400,
                      }}
                    >
                      {job.text}
                    </span>
                  ))}
                  {option?.title ? ` - ${option.title}` : null}
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
    </>
  );
};
export default FormikTaskAutoComplete;
